// components/TradingCommand/MarketChart.jsx
// v3 — Rich AI story layer: fixed tooltip, confidence markers, regime labels,
// support/resistance zones, mind-change indicators, risk flag counts.

import React, { memo, useMemo, useState, useEffect, useRef } from "react";
import ReactApexChart from "react-apexcharts";

// ============================================================================
// DESIGN TOKENS
// ============================================================================
const C = {
  bg:     "#08101e",
  bg1:    "#0c1525",
  bg2:    "#101b2e",
  border: "#1e2d42",
  text:   "#e8edf5",
  body:   "#b8c8dc",
  meta:   "#6a7d95",
  green:  "#00e896",
  cyan:   "#00c8ff",
  amber:  "#ffb340",
  red:    "#ff4466",
  blue:   "#4488ff",
  purple: "#9966ff",
  font:   "'DM Mono', monospace",
};

// ============================================================================
// HELPERS
// ============================================================================

const confColor = (bucket) => ({
  VERY_HIGH: C.green, HIGH: C.green, MEDIUM: C.cyan, LOW: C.amber, VERY_LOW: C.red,
})[bucket] || C.meta;

const confMarkerSize = (bucket) => ({
  VERY_HIGH: 12, HIGH: 10, MEDIUM: 8, LOW: 6, VERY_LOW: 5,
})[bucket] || 7;

const regimeColor = (regime) => ({
  STRONG_TREND: C.green, WEAK_TREND: C.cyan, SIDEWAYS: C.purple,
  VOLATILE: C.amber, CHOPPY: C.red,
})[regime] || C.meta;

const countRiskFlags = (rf) => rf ? Object.values(rf).filter(Boolean).length : 0;

const fmt = (v, dec = 2) => (v != null && !isNaN(v) ? Number(v).toFixed(dec) : "—");

function extractStory(bar) {
  if (!bar) return null;
  const story = bar.eval_agent_story;
  if (!story) return null;

  const llm        = story.chart_analysis?.llm_full_response || {};
  const regime     = llm.regime_analysis || {};
  const overall    = llm.overall_assessment || {};
  const riskF      = llm.risk_factors || {};
  const thinking   = story.thinking_snapshots || {};
  const initialT   = thinking.initial_thinking || {};
  const finalT     = thinking.final_thinking || {};
  const combined   = thinking.combined_thinking || {};
  const memSummary = story.memory_summary || story.memory || {};
  const perf       = story.performance || {};
  const decision   = story.decision || {};

  const initialDecision = initialT.provisional_decision || "";
  const finalDecision   = finalT.decision || decision.final || bar.eval_decision || "";
  const mindChanged     = !!(initialDecision && finalDecision && initialDecision !== finalDecision);

  return {
    decision:         finalDecision,
    confidence:       decision.confidence ?? null,
    confidenceBucket: decision.confidence_bucket || initialT.confidence_bucket || null,
    regime:           regime.regime_classification || null,
    optionStrategy:   llm.option_strategy || null,
    scale:            llm.scale || null,
    supportLevel:     overall.key_support_level || null,
    resistanceLevel:  overall.key_resistance_level || null,
    riskFlags:        riskF,
    riskFlagCount:    countRiskFlags(riskF),
    patterns:         llm.identified_patterns || [],
    memCases:         memSummary.found_cases ?? 0,
    topSimilarity:    memSummary.top_similarity ?? 0,
    mindChanged,
    initialDecision,
    finalDecision,
    changeDriver:     combined.consistency_recommendation || "",
    ruleVotes:        initialT.rule_votes || {},
    supportsDecision: perf.supports_decision ?? null,
    targetHitRate:    perf.target_hit_rate ?? null,
    avgPnl:           perf.avg_pnl ?? null,
  };
}

function buildEvalMap(data) {
  const map = {};
  if (!data) return map;
  data.forEach((bar) => {
    if (bar.eval_agent_story || bar.eval_decision) {
      const ts = new Date(bar.t).getTime();
      map[ts] = { bar, story: extractStory(bar), decision: bar.eval_decision,
                  close: parseFloat(bar.close || bar.c || 0) };
    }
  });
  return map;
}

// ============================================================================
// TOOLTIP HTML BUILDERS
// ============================================================================
const tr = (label, value, color = C.body) =>
  `<div style="display:flex;justify-content:space-between;gap:12px;padding:2px 0">
    <span style="color:${C.meta};font-size:10px">${label}</span>
    <span style="color:${color};font-size:10px;font-weight:500">${value}</span>
  </div>`;

const divider = () => `<div style="border-top:1px solid ${C.border};margin:5px 0"></div>`;

const tpill = (text, color) =>
  `<span style="display:inline-block;padding:1px 5px;font-size:9px;
   background:${color}18;border:1px solid ${color}50;border-radius:3px;
   color:${color};font-weight:500;margin-right:3px">${text}</span>`;

function buildStorySection(s) {
  if (!s) return "";
  let html = "";

  html += divider();

  const decColor = { PROCEED: C.green, HOLD: C.cyan, ADJUST: C.amber,
                     CLOSE: C.red, SKIP: C.meta }[s.decision] || C.meta;
  html += `<div style="margin-bottom:5px">
    ${tpill(s.decision || "—", decColor)}
    ${s.confidenceBucket ? tpill(s.confidenceBucket, confColor(s.confidenceBucket)) : ""}
    ${s.regime ? tpill(s.regime.replace(/_/g, " "), regimeColor(s.regime)) : ""}
  </div>`;

  if (s.mindChanged) {
    html += `<div style="color:${C.amber};font-size:9px;margin-bottom:5px">
      Mind change: ${s.initialDecision} → ${s.finalDecision}
      ${s.changeDriver ? `· ${s.changeDriver}` : ""}
    </div>`;
  }

  if (s.optionStrategy || s.scale != null) {
    html += `<div style="margin-bottom:5px">
      ${s.optionStrategy ? tpill(s.optionStrategy.replace(/_/g, " "), C.blue) : ""}
      ${s.scale != null ? tpill(`SCALE ${Math.round(s.scale * 100)}%`, s.scale < 1 ? C.amber : C.cyan) : ""}
    </div>`;
  }

  html += divider();

  const memColor = s.memCases === 0 ? C.red : s.memCases < 5 ? C.amber : C.green;
  html += tr("Memory", s.memCases === 0
    ? "No prior cases" : `${s.memCases} cases (${fmt(s.topSimilarity * 100, 0)}% match)`, memColor);

  if (s.supportLevel)    html += tr("Support",    `$${fmt(s.supportLevel)}`,    C.green + "cc");
  if (s.resistanceLevel) html += tr("Resistance", `$${fmt(s.resistanceLevel)}`, C.red + "cc");

  if (s.riskFlagCount > 0) {
    html += tr("Risk flags", `${s.riskFlagCount}/6 active`,
      s.riskFlagCount >= 4 ? C.red : s.riskFlagCount >= 2 ? C.amber : C.meta);
  }

  const votes = s.ruleVotes;
  if (votes && Object.keys(votes).length > 0) {
    html += divider();
    html += `<div style="color:${C.meta};font-size:9px;margin-bottom:3px;letter-spacing:0.05em">RULE VOTES</div>`;
    Object.entries(votes).forEach(([k, v]) => {
      const pct   = Math.round((v || 0) * 100);
      const label = k.replace(/_/g, " ").toUpperCase();
      const w     = Math.min(pct * 0.7, 70);
      html += `<div style="display:flex;justify-content:space-between;align-items:center;gap:6px;margin-bottom:2px">
        <span style="color:${C.meta};font-size:9px;min-width:80px">${label}</span>
        <div style="display:flex;align-items:center;gap:4px">
          <div style="width:${w}px;height:2px;background:${C.cyan}60;border-radius:1px"></div>
          <span style="color:${C.body};font-size:9px">${pct}%</span>
        </div>
      </div>`;
    });
  }

  if (s.targetHitRate != null) {
    html += divider();
    html += tr("Target hit rate", `${fmt(s.targetHitRate * 100, 0)}%`,
      s.targetHitRate > 0.6 ? C.green : C.amber);
    if (s.avgPnl != null)
      html += tr("Avg P&L", `$${fmt(s.avgPnl)}`, s.avgPnl >= 0 ? C.green : C.red);
  }

  return html;
}

function buildCandleTooltip(o, h, l, c, timeStr, evalData) {
  const isUp = (c || 0) >= (o || 0);
  return `<div style="background:${C.bg1};border:1px solid ${C.border};padding:10px 14px;
    font-family:${C.font};min-width:190px">
    <div style="color:${C.meta};font-size:10px;margin-bottom:6px">${timeStr}</div>
    ${tr("O", `$${fmt(o)}`)}
    ${tr("H", `$${fmt(h)}`, C.green)}
    ${tr("L", `$${fmt(l)}`, C.red)}
    ${tr("C", `$${fmt(c)}`, isUp ? C.green : C.red)}
    ${evalData ? buildStorySection(evalData.story) : ""}
  </div>`;
}

function buildLineTooltip(closeVal, timeStr, evalData) {
  return `<div style="background:${C.bg1};border:1px solid ${C.border};padding:10px 14px;
    font-family:${C.font};min-width:190px">
    <div style="color:${C.meta};font-size:10px;margin-bottom:6px">${timeStr}</div>
    ${tr("Price", `$${fmt(closeVal)}`, C.text)}
    ${evalData ? buildStorySection(evalData.story) : ""}
  </div>`;
}

// ============================================================================
// PILL COMPONENT
// ============================================================================
const Pill = ({ children, color = C.meta }) => (
  <span style={{
    display: "inline-flex", alignItems: "center", padding: "2px 7px",
    fontSize: "9px", fontFamily: C.font, fontWeight: 500,
    letterSpacing: "0.04em", textTransform: "uppercase",
    color, background: color + "18", border: `1px solid ${color}50`,
    borderRadius: "4px", lineHeight: "16px",
  }}>{children}</span>
);

// ============================================================================
// TOGGLE BUTTON
// ============================================================================
const ToggleBtn = ({ label, active, onClick }) => (
  <button onClick={onClick} style={{
    padding: "3px 10px", fontSize: "10px", fontFamily: C.font,
    fontWeight: active ? 500 : 400,
    color: active ? C.text : C.meta,
    background: active ? C.bg2 : "transparent",
    border: `1px solid ${active ? C.border : "transparent"}`,
    borderRadius: "4px", cursor: "pointer", transition: "all 0.15s", lineHeight: "18px",
  }}>{label}</button>
);

// ============================================================================
// GUIDE PANEL SUB-COMPONENTS
// ============================================================================

const CircleSymbol = ({ fill, stroke, size = 8 }) => (
  <svg width={size + 4} height={size + 4} style={{ flexShrink: 0, display: "block" }}>
    <circle cx={(size + 4) / 2} cy={(size + 4) / 2} r={size / 2}
      fill={fill} stroke={stroke} strokeWidth="1.5" />
  </svg>
);

const AdjSymbol = ({ color, star }) => (
  <span style={{
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    width: 24, height: 24, borderRadius: "50%", flexShrink: 0,
    background: star ? "transparent" : color + "25",
    border: `${star ? 2 : 1.5}px solid ${color}`,
    fontSize: "7px", fontFamily: C.font, fontWeight: 600, color,
  }}>{star ? "ADJ*" : "ADJ"}</span>
);

const LineSymbol = ({ color, dashed, thick }) => (
  <svg width={32} height={12} style={{ flexShrink: 0, display: "block" }}>
    <line x1="0" y1="6" x2="32" y2="6"
      stroke={color} strokeWidth={thick ? 2.5 : 1}
      strokeDasharray={dashed ? "5,3" : "none"} />
  </svg>
);

// Price levels SVG diagram — shows all six line types annotated
const PriceLevelDiagram = () => {
  const lines = [
    { y: 20,  color: C.green,  dash: "5,3", thick: 2.5, label: "Target (original)",  side: "right" },
    { y: 44,  color: C.cyan,   dash: "none", thick: 2.5, label: "Target (adjusted)",  side: "right" },
    { y: 68,  color: C.red,    dash: "5,3", thick: 2.5, label: "Stop (original)",     side: "right" },
    { y: 92,  color: C.red,    dash: "none", thick: 2.5, label: "Stop (adjusted)",    side: "right" },
    { y: 118, color: C.green,  dash: "5,3", thick: 1,   label: "S — Support zone",    side: "right" },
    { y: 142, color: C.red,    dash: "5,3", thick: 1,   label: "R — Resistance zone", side: "right" },
  ];
  return (
    <svg width="100%" viewBox="0 0 300 162" style={{ display: "block", overflow: "visible" }}>
      {lines.map(({ y, color, dash, thick, label }) => (
        <g key={y}>
          <line x1="0" y1={y} x2="180" y2={y}
            stroke={color} strokeWidth={thick}
            strokeDasharray={dash === "none" ? undefined : dash} />
          <text x="188" y={y + 4} fill={color}
            fontSize="10" fontFamily="'DM Mono', monospace">{label}</text>
        </g>
      ))}
    </svg>
  );
};

const GuideSection = ({ title, color, children }) => (
  <div style={{ marginBottom: "24px" }}>
    <div style={{
      fontSize: "8px", color, letterSpacing: "0.12em", textTransform: "uppercase",
      marginBottom: "12px", paddingBottom: "5px", borderBottom: `1px solid ${color}30`,
    }}>{title}</div>
    <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
      {children}
    </div>
  </div>
);

const GuideRow = ({ symbol, heading, body }) => (
  <div style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
    <div style={{ paddingTop: "1px", flexShrink: 0 }}>{symbol}</div>
    <div>
      <div style={{ fontSize: "11px", color: C.body, fontWeight: 500, marginBottom: "3px" }}>
        {heading}
      </div>
      <div style={{ fontSize: "10px", color: C.meta, lineHeight: "1.55" }}>{body}</div>
    </div>
  </div>
);

// Confidence scale — five circles showing size/color progression
const ConfidenceScale = () => (
  <div style={{ display: "flex", alignItems: "flex-end", gap: "10px", margin: "8px 0 4px" }}>
    {[
      { size: 5,  fill: C.red,   label: "VERY LOW" },
      { size: 6,  fill: C.amber, label: "LOW" },
      { size: 8,  fill: C.cyan,  label: "MEDIUM" },
      { size: 10, fill: C.green, label: "HIGH" },
      { size: 12, fill: C.green, label: "VERY HIGH" },
    ].map(({ size, fill, label }) => (
      <div key={label} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "5px" }}>
        <svg width={14} height={14}>
          <circle cx={7} cy={7} r={size / 2} fill={fill} stroke={fill} strokeWidth="1" />
        </svg>
        <span style={{ fontSize: "7px", color: C.meta, textAlign: "center",
          fontFamily: C.font, letterSpacing: "0.03em", lineHeight: 1.2 }}>{label}</span>
      </div>
    ))}
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "5px", marginLeft: "6px" }}>
      <svg width={14} height={14}>
        <circle cx={7} cy={7} r={5} fill={C.bg1} stroke={C.cyan} strokeWidth="1.5" />
      </svg>
      <span style={{ fontSize: "7px", color: C.meta, textAlign: "center",
        fontFamily: C.font, letterSpacing: "0.03em", lineHeight: 1.2 }}>HOLLOW<br/>= no mem</span>
    </div>
  </div>
);

// The main slide-in panel — rendered via portal-style fixed overlay
const ChartGuidePanel = ({ open, onClose }) => {
  const panelRef = useRef(null);

  // Escape key closes
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Click outside panel closes
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) onClose();
    };
    // Small delay so the open-trigger click doesn't immediately close it
    const t = setTimeout(() => document.addEventListener("mousedown", onDown), 50);
    return () => { clearTimeout(t); document.removeEventListener("mousedown", onDown); };
  }, [open, onClose]);

  return (
    /* Slide-in panel — no backdrop, chart stays fully visible */
    <div
      ref={panelRef}
      style={{
        position: "fixed", top: 0, left: 0, bottom: 0, zIndex: 1001,
        width: "340px",
        background: C.bg1,
        borderRight: `1px solid ${C.border}`,
        transform: open ? "translateX(0)" : "translateX(-100%)",
        transition: "transform 0.22s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
        display: "flex", flexDirection: "column",
        fontFamily: C.font,
        boxShadow: open ? "6px 0 40px rgba(0,0,0,0.55)" : "none",
        pointerEvents: open ? "auto" : "none",
      }}
    >

        {/* Panel header */}
        <div style={{
          padding: "18px 20px 14px",
          borderBottom: `1px solid ${C.border}`,
          display: "flex", alignItems: "flex-start", justifyContent: "space-between",
          flexShrink: 0,
        }}>
          <div>
            <div style={{
              fontSize: "8px", color: C.meta, letterSpacing: "0.12em",
              textTransform: "uppercase", marginBottom: "5px",
            }}>CHART INTELLIGENCE GUIDE</div>
            <div style={{ fontSize: "12px", color: C.text, lineHeight: "1.55", maxWidth: "260px" }}>
              Every marker is produced by a five-layer AI — not by indicators.
              Confidence, regime, memory, and risk are all encoded on the chart surface.
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "transparent", border: `1px solid ${C.border}`,
              borderRadius: "4px", color: C.meta, fontFamily: C.font,
              fontSize: "11px", padding: "3px 8px", cursor: "pointer",
              flexShrink: 0, marginLeft: "10px", marginTop: "2px",
              transition: "border-color 0.15s, color 0.15s",
            }}
            onMouseEnter={e => { e.target.style.borderColor = C.text; e.target.style.color = C.text; }}
            onMouseLeave={e => { e.target.style.borderColor = C.border; e.target.style.color = C.meta; }}
          >✕</button>
        </div>

        {/* Scrollable content */}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px", scrollbarWidth: "thin",
          scrollbarColor: `${C.border} transparent` }}>

          {/* SECTION 1 — Entry Markers */}
          <GuideSection title="ENTRY MARKERS" color={C.cyan}>
            <div style={{ fontSize: "10px", color: C.meta, lineHeight: "1.55", marginBottom: "4px" }}>
              Circle size encodes confidence. Fill encodes memory status.
            </div>
            <ConfidenceScale />
            <GuideRow
              symbol={<CircleSymbol fill={C.green} stroke={C.green} size={12} />}
              heading="Large filled = high confidence + memory found"
              body="The AI entered with strong conviction and found similar historical trades to validate the signal." />
            <GuideRow
              symbol={<CircleSymbol fill={C.bg1} stroke={C.cyan} size={8} />}
              heading="Hollow ring = no memory cases"
              body="Zero similar past trades found. The AI is flying on model signals alone — treat with caution regardless of stated confidence." />
            <GuideRow
              symbol={
                <span style={{ fontSize: "8px", color: C.purple, background: C.bg,
                  padding: "1px 4px", border: `1px solid ${C.purple}40`, borderRadius: "2px",
                  fontFamily: C.font, flexShrink: 0 }}>SIDEWAYS</span>
              }
              heading="Regime label above marker"
              body="Market regime at entry: STRONG TREND, WEAK TREND, SIDEWAYS, VOLATILE, or CHOPPY. The AI scales position size and picks option strategy based on regime." />
          </GuideSection>

          {/* SECTION 2 — Adjustment Markers */}
          <GuideSection title="ADJUSTMENT MARKERS" color={C.amber}>
            <GuideRow
              symbol={<AdjSymbol color={C.cyan} star={false} />}
              heading="ADJ (cyan) — clean adjustment"
              body="Target or stop updated with 0–1 active risk flags. Low-risk repositioning from new price action or pattern confirmation." />
            <GuideRow
              symbol={<AdjSymbol color={C.amber} star={false} />}
              heading="ADJ (amber) — moderate risk"
              body="2–3 risk flags active at adjustment time. The AI moved levels despite elevated risk. Hover the marker to see which flags triggered." />
            <GuideRow
              symbol={<AdjSymbol color={C.red} star={true} />}
              heading="ADJ* (red hollow) — mind changed + high risk"
              body="4+ risk flags active AND the AI overrode its initial decision. It started as HOLD then the consistency review layer intervened. The asterisk marks this second-opinion override." />
          </GuideSection>

          {/* SECTION 3 — Price Levels */}
          <GuideSection title="PRICE LEVELS" color={C.meta}>
            <div style={{ fontSize: "10px", color: C.meta, lineHeight: "1.55", marginBottom: "8px" }}>
              Six line types. Thick lines are trade levels. Thin lines are structural zones the AI was monitoring.
            </div>
            <PriceLevelDiagram />
            <div style={{
              marginTop: "12px", padding: "10px 12px",
              background: C.bg, border: `1px solid ${C.border}`, borderRadius: "6px",
              fontSize: "9px", color: C.meta, lineHeight: "1.6",
            }}>
              S and R lines are not drawn from chart patterns — they come from the AI's
              microstructure analysis of barrier proximity, range tightness, and order flow.
              They update each time the AI evaluates the position.
            </div>
          </GuideSection>

          {/* Footer */}
          <div style={{
            paddingTop: "14px", borderTop: `1px solid ${C.border}`,
            fontSize: "9px", color: C.meta, lineHeight: "1.6",
          }}>
            <div style={{ marginBottom: "6px", color: C.body, fontSize: "10px" }}>Controls</div>
            <div style={{ marginBottom: "4px" }}>
              <span style={{ color: C.body }}>CANDLE / LINE / AREA</span> — changes price display only.
              All AI markers stay visible in every mode.
            </div>
            <div style={{ marginBottom: "4px" }}>
              <span style={{ color: C.body }}>LAST 30 / LAST 60</span> — windows the visible range.
              Full session data is always loaded — zoom and pan freely.
            </div>
            <div>
              <span style={{ color: C.body }}>FULL DAY</span> — recommended in session review
              to see the complete trade arc. Use LAST 30 in live trading.
            </div>
          </div>
        </div>
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const MarketChart = ({
  data,
  selectedPosition,
  defaultChartType  = "candlestick",
  defaultWindow     = "last30",
  defaultShowGuide  = false,
}) => {

  const [chartType,   setChartType]   = useState(defaultChartType);
  const [viewWindow,  setViewWindow]  = useState(defaultWindow);
  const [xRange,      setXRange]      = useState({ min: undefined, max: undefined });
  const [showGuide,   setShowGuide]   = useState(defaultShowGuide);

  useEffect(() => {
    if (!data || data.length === 0) return;
    if (viewWindow === "full") { setXRange({ min: undefined, max: undefined }); return; }
    const barCount = viewWindow === "last30" ? 30 : 60;
    const lastTs   = new Date(data[data.length - 1].t).getTime();
    const firstTs  = lastTs - (barCount - 1) * 60_000;
    setXRange({ min: firstTs, max: lastTs + 2 * 60_000 });
  }, [data, viewWindow]);

  // ── Eval map ──────────────────────────────────────────────────────────────
  const evalMap = useMemo(() => buildEvalMap(data), [data]);

  // ── Adjustment extraction ─────────────────────────────────────────────────
  const allAdjustmentsData = useMemo(() => {
    if (!data || data.length === 0) return [];
    const byPos = {};
    data.forEach((bar, index) => {
      const posId = bar.eval_position_id || bar.position_id;
      if (posId && bar.eval_decision === "ADJUST") {
        if (!byPos[posId]) byPos[posId] = { adjustments: [], latestTarget: 0, latestStop: 0 };
        const pd  = byPos[posId];
        const adj = {
          positionId: posId,
          timestamp:  new Date(bar.t).getTime(),
          newTarget:  parseFloat(bar.eval_new_target || bar.tp || bar.target || 0),
          newStop:    parseFloat(bar.eval_new_stop_loss || bar.sl || bar.stop_loss || 0),
          price:      parseFloat(bar.close || bar.c || 0),
          barIndex:   index,
          story:      extractStory(bar),
        };
        pd.adjustments.push(adj);
        if (adj.newTarget) pd.latestTarget = adj.newTarget;
        if (adj.newStop)   pd.latestStop   = adj.newStop;
      }
    });
    const all = [];
    Object.values(byPos).forEach(p => all.push(...p.adjustments));
    return all;
  }, [data]);

  const selectedPositionData = useMemo(() => {
    if (!selectedPosition || !data || data.length === 0)
      return { adjustments: [], currentTarget: null, currentStop: null };
    const posId = selectedPosition.id || selectedPosition.a_id;
    const adjustments = [];
    let latestTarget = selectedPosition.target || selectedPosition.tp || 0;
    let latestStop   = selectedPosition.stop   || selectedPosition.sl || 0;
    data.forEach((bar) => {
      if ((bar.eval_position_id || bar.position_id) === posId && bar.eval_decision === "ADJUST") {
        const adj = {
          timestamp: new Date(bar.t).getTime(),
          newTarget: bar.eval_new_target || bar.tp || bar.target || latestTarget,
          newStop:   bar.eval_new_stop_loss || bar.sl || bar.stop_loss || latestStop,
          price:     parseFloat(bar.close || 0),
          story:     extractStory(bar),
        };
        adjustments.push(adj);
        if (adj.newTarget) latestTarget = adj.newTarget;
        if (adj.newStop)   latestStop   = adj.newStop;
      }
    });
    return {
      adjustments, currentTarget: latestTarget, currentStop: latestStop,
      originalTarget: selectedPosition.target || selectedPosition.tp || 0,
      originalStop:   selectedPosition.stop   || selectedPosition.sl || 0,
    };
  }, [selectedPosition, data]);

  // ── PROCEED bars ──────────────────────────────────────────────────────────
  const proceedBars = useMemo(() => {
    if (!data || data.length === 0) return [];
    return data
      .filter(bar => bar.eval_decision === "PROCEED" || bar.eval_decision === "BUY")
      .map(bar => ({
        timestamp: new Date(bar.t).getTime(),
        price:     parseFloat(bar.close || bar.c || 0),
        story:     extractStory(bar),
      }));
  }, [data]);

  // ── Series data ───────────────────────────────────────────────────────────
  const ohlc = useMemo(() => {
    if (!data || data.length === 0) return [];
    return data.map(({ t, open, high, low, close }) => ({
      x: new Date(t).getTime(),
      y: [parseFloat(open)||0, parseFloat(high)||0, parseFloat(low)||0, parseFloat(close)||0],
    }));
  }, [data]);

  const closeLine = useMemo(() => {
    if (!data || data.length === 0) return [];
    return data.map(({ t, close }) => ({ x: new Date(t).getTime(), y: parseFloat(close)||null }));
  }, [data]);

  const makePredLine = (field) => {
    if (!data || data.length === 0) return [];
    return data.map((bar) => {
      const raw = bar[field];
      if (!raw || raw === "null" || raw === "NaN") return { x: new Date(bar.t).getTime(), y: null };
      const v = parseFloat(raw);
      return (isNaN(v) || v <= 0) ? { x: new Date(bar.t).getTime(), y: null } : { x: new Date(bar.t).getTime(), y: v };
    });
  };

  const up   = useMemo(() => makePredLine("up"),   [data]);
  const down = useMemo(() => makePredLine("down"),  [data]);
  const pv   = useMemo(() => makePredLine("pv"),    [data]);

  const latestData = useMemo(() => (!data || data.length === 0) ? {} : data[data.length - 1] || {}, [data]);
  const genAI = latestData.gen_ai_msg || {};

  // ── Latest regime for header pill ─────────────────────────────────────────
  const latestEval = useMemo(() => {
    if (!data || data.length === 0) return null;
    for (let i = data.length - 1; i >= 0; i--) {
      const s = extractStory(data[i]);
      if (s?.regime) return s;
    }
    return null;
  }, [data]);

  // ── Annotations ───────────────────────────────────────────────────────────
  const positionAnnotations = useMemo(() => {
    const ann = { yaxis: [], xaxis: [], points: [] };

    // Support / resistance lines — deduplicated across all eval bars
    const srSeen = new Set();
    if (data) {
      data.forEach((bar) => {
        const s = extractStory(bar);
        if (!s) return;
        [
          { type: "support",    val: s.supportLevel },
          { type: "resistance", val: s.resistanceLevel },
        ].forEach(({ type, val }) => {
          if (!val) return;
          const key = `${type}_${Math.round(val * 10)}`;
          if (srSeen.has(key)) return;
          srSeen.add(key);
          ann.yaxis.push({
            y:               val,
            borderColor:     type === "support" ? C.green + "70" : C.red + "70",
            borderWidth:     1,
            strokeDashArray: 6,
            label: {
              borderColor: "transparent",
              style: { color: type === "support" ? C.green : C.red,
                       background: "transparent", fontSize: "9px", fontFamily: C.font },
              text:     type === "support" ? `S ${fmt(val)}` : `R ${fmt(val)}`,
              position: "right",
              offsetX:  -6,
            },
          });
        });
      });
    }

    // ── No position selected — GenAI + PROCEED markers ───────────────────────
    if (!selectedPosition) {
      if (genAI.entry_price) {
        ann.yaxis.push({ y: parseFloat(genAI.entry_price), borderColor: C.amber, strokeDashArray: 0,
          label: { borderColor: C.amber,
            style: { color: C.bg, background: C.amber, fontSize: "10px", fontFamily: C.font },
            text: `Entry $${parseFloat(genAI.entry_price).toFixed(2)}` } });
      }
      if (genAI.take_profit) {
        ann.yaxis.push({ y: parseFloat(genAI.take_profit), borderColor: C.green, strokeDashArray: 4,
          label: { borderColor: C.green,
            style: { color: C.bg, background: C.green, fontSize: "10px", fontFamily: C.font },
            text: `Target $${parseFloat(genAI.take_profit).toFixed(2)}` } });
      }
      if (genAI.stop_loss) {
        ann.yaxis.push({ y: parseFloat(genAI.stop_loss), borderColor: C.red, strokeDashArray: 4,
          label: { borderColor: C.red,
            style: { color: C.text, background: C.red, fontSize: "10px", fontFamily: C.font },
            text: `Stop $${parseFloat(genAI.stop_loss).toFixed(2)}` } });
      }

      proceedBars.forEach((pb) => {
        const s      = pb.story;
        const bucket = s?.confidenceBucket || "MEDIUM";
        const color  = confColor(bucket);
        const size   = confMarkerSize(bucket);
        const memOk  = (s?.memCases || 0) > 0;
        ann.points.push({
          x: pb.timestamp, y: pb.price,
          marker: { size, fillColor: memOk ? color : C.bg1,
                    strokeColor: color, strokeWidth: 2, shape: "circle" },
          label: s?.regime ? {
            text: s.regime.replace(/_/g, " "), offsetY: -(size + 6),
            style: { fontSize: "8px", color: regimeColor(s.regime), background: C.bg,
                     fontFamily: C.font, padding: { left: 3, right: 3, top: 1, bottom: 1 } },
          } : undefined,
        });
      });

      return ann;
    }

    // ── Position selected ────────────────────────────────────────────────────
    const pos = selectedPosition;
    const { adjustments, currentTarget, currentStop, originalTarget, originalStop } = selectedPositionData;

    if (pos.entryPrice) {
      ann.yaxis.push({ y: pos.entryPrice, borderColor: C.amber, borderWidth: 2, strokeDashArray: 0,
        label: { borderColor: C.amber,
          style: { color: C.bg, background: C.amber, fontSize: "10px", fontFamily: C.font },
          text: `Entry $${pos.entryPrice.toFixed(2)}` } });
    }
    if (originalTarget > 0) {
      ann.yaxis.push({ y: originalTarget, borderColor: C.green, borderWidth: 2, strokeDashArray: 4,
        label: { borderColor: C.green,
          style: { color: C.bg, background: C.green, fontSize: "10px", fontFamily: C.font },
          text: `Target $${originalTarget.toFixed(2)}` } });
    }
    if (adjustments.length > 0 && currentTarget && currentTarget !== originalTarget) {
      ann.yaxis.push({ y: currentTarget, borderColor: C.cyan, borderWidth: 2, strokeDashArray: 0,
        label: { borderColor: C.cyan,
          style: { color: C.bg, background: C.cyan, fontSize: "10px", fontFamily: C.font },
          text: `Adj Target $${currentTarget.toFixed(2)}` } });
    }
    if (originalStop > 0) {
      ann.yaxis.push({ y: originalStop, borderColor: C.red, borderWidth: 2, strokeDashArray: 4,
        label: { borderColor: C.red,
          style: { color: C.text, background: C.red, fontSize: "10px", fontFamily: C.font },
          text: `Stop $${originalStop.toFixed(2)}` } });
    }
    if (adjustments.length > 0 && currentStop && currentStop !== originalStop) {
      ann.yaxis.push({ y: currentStop, borderColor: C.red, borderWidth: 3, strokeDashArray: 0,
        label: { borderColor: C.red,
          style: { color: C.text, background: C.red, fontSize: "10px", fontFamily: C.font, fontWeight: "bold" },
          text: `Adj Stop $${currentStop.toFixed(2)}` } });
    }

    if (pos.entryTime) {
      const entryTs   = new Date(pos.entryTime).getTime();
      const entryEval = evalMap[entryTs];
      const bucket    = entryEval?.story?.confidenceBucket || "MEDIUM";
      const color     = confColor(bucket);
      const size      = confMarkerSize(bucket);
      const memOk     = (entryEval?.story?.memCases || 0) > 0;
      const regime    = entryEval?.story?.regime;

      ann.xaxis.push({ x: entryTs, borderColor: C.amber, borderWidth: 2, strokeDashArray: 4,
        label: { borderColor: C.amber,
          style: { color: C.bg, background: C.amber, fontSize: "10px", fontFamily: C.font, fontWeight: "bold" },
          text: "Entry", orientation: "horizontal" } });

      if (pos.entryPrice) {
        ann.points.push({
          x: entryTs, y: pos.entryPrice,
          marker: { size, fillColor: memOk ? color : C.bg1,
                    strokeColor: color, strokeWidth: 2, shape: "circle" },
          label: regime ? {
            text: regime.replace(/_/g, " "), offsetY: -(size + 6),
            style: { fontSize: "8px", color: regimeColor(regime), background: C.bg,
                     fontFamily: C.font, padding: { left: 3, right: 3, top: 1, bottom: 1 } },
          } : undefined,
        });
      }
    }

    if (pos.exitTime) {
      const exitTs    = new Date(pos.exitTime).getTime();
      const exitColor = pos.isWin ? C.green : C.red;
      ann.xaxis.push({ x: exitTs, borderColor: exitColor, borderWidth: 2, strokeDashArray: 4,
        label: { borderColor: exitColor,
          style: { color: pos.isWin ? C.bg : C.text, background: exitColor,
                   fontSize: "10px", fontFamily: C.font, fontWeight: "bold" },
          text: "Exit", orientation: "horizontal" } });
      if (pos.exitPrice) {
        ann.points.push({ x: exitTs, y: pos.exitPrice,
          marker: { size: 8, fillColor: exitColor, strokeColor: C.text, strokeWidth: 2, shape: "circle" } });
      }
    }

    // ADJUST markers — risk-aware + mind-change
    allAdjustmentsData.forEach((adj) => {
      const s         = adj.story;
      const riskCount = s?.riskFlagCount || 0;
      const changed   = s?.mindChanged || false;
      const mColor    = riskCount >= 4 ? C.red : riskCount >= 2 ? C.amber : C.cyan;
      const size      = riskCount >= 4 ? 11 : riskCount >= 2 ? 9 : 7;

      ann.xaxis.push({ x: adj.timestamp, borderColor: mColor, strokeDashArray: 2, borderWidth: 1,
        label: { borderColor: mColor,
          style: { color: C.bg, background: mColor, fontSize: "9px", fontFamily: C.font },
          text: changed ? "ADJ*" : "ADJ", orientation: "horizontal", offsetY: 0 } });

      ann.points.push({
        x: adj.timestamp, y: adj.price,
        marker: { size, fillColor: changed ? C.bg1 : mColor,
                  strokeColor: mColor, strokeWidth: changed ? 3 : 2, shape: "circle" },
        label: {
          text: `S ${fmt(adj.newStop)}`, offsetY: size + 8,
          style: { fontSize: "9px", color: mColor, background: C.bg, fontFamily: C.font,
                   padding: { left: 3, right: 3, top: 1, bottom: 1 } },
        },
      });
    });

    adjustments.forEach((adj, i) => {
      ann.points.push({
        x: adj.timestamp, y: adj.newTarget || adj.price,
        marker: { size: 10, fillColor: C.cyan, strokeColor: C.text, strokeWidth: 2, shape: "circle" },
        label: { text: `Adj ${i + 1}`, offsetY: -18,
          style: { fontSize: "9px", color: C.bg, background: C.cyan, fontFamily: C.font,
                   padding: { left: 3, right: 3, top: 1, bottom: 1 } } },
      });
    });

    return ann;
  }, [selectedPosition, genAI, selectedPositionData, allAdjustmentsData, proceedBars, evalMap, data]);

  // ── Chart options ─────────────────────────────────────────────────────────
  const chartOptions = useMemo(() => {
    const isCandle = chartType === "candlestick";

    return {
      chart: {
        type: isCandle ? "candlestick" : "line",
        height: 400, background: C.bg1, foreColor: C.body, fontFamily: C.font,
        zoom: { enabled: true, type: "x" }, animations: { enabled: false },
        toolbar: { show: true,
          tools: { download: true, selection: true, zoom: true, zoomin: true, zoomout: true, pan: true, reset: true } },
      },
      theme: { mode: "dark" },
      grid: { show: true, borderColor: C.border, strokeDashArray: 3,
        xaxis: { lines: { show: true } }, yaxis: { lines: { show: false } } },
      plotOptions: {
        candlestick: { colors: { upward: C.green, downward: C.red }, wick: { useFillColor: true } },
      },
      stroke: {
        width: isCandle ? [1, 1.5, 1.5, 1.5] : [2, 1.5, 1.5, 1.5],
        curve: "smooth",
        colors: isCandle ? [C.meta, C.green, C.red, C.amber] : [C.cyan, C.green, C.red, C.amber],
      },
      fill: isCandle
        ? { type: ["solid", "solid", "solid", "solid"] }
        : { type: ["gradient", "solid", "solid", "solid"],
            gradient: { shade: "dark", type: "vertical", shadeIntensity: 0.3,
              gradientToColors: [C.bg1],
              opacityFrom: chartType === "area" ? 0.3 : 0, opacityTo: 0, stops: [0, 100] } },
      markers: { size: isCandle ? [0,0,0,0] : [0,3,3,3],
                 strokeWidth: 1, strokeColors: C.bg, hover: { size: 5 } },
      xaxis: {
        type: "datetime", min: xRange.min, max: xRange.max,
        labels: {
          formatter: (val) => new Date(val).toLocaleTimeString("en-US",
            { hour: "numeric", minute: "numeric", hour12: false }),
          style: { colors: C.meta, fontSize: "10px", fontFamily: C.font },
        },
        axisBorder: { show: true, color: C.border },
        axisTicks:  { show: true, color: C.border },
      },
      yaxis: {
        tooltip: { enabled: true },
        labels: { formatter: (v) => v == null ? "" : "$" + v.toFixed(2),
                  style: { colors: C.meta, fontSize: "10px", fontFamily: C.font } },
        axisBorder: { show: true, color: C.border },
      },
      tooltip: {
        enabled: true,
        theme: "dark",
        shared: false,       // IMPORTANT: false prevents empty-string collision across series
        intersect: false,
        followCursor: true,
        style: { fontSize: "11px", fontFamily: C.font },
        custom: function ({ seriesIndex, dataPointIndex, w }) {
          const ts      = w.globals.seriesX[seriesIndex]?.[dataPointIndex];
          const timeStr = ts ? new Date(ts).toLocaleTimeString("en-US",
            { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "";
          const evalData = ts ? evalMap[ts] : null;

          // Candlestick price series
          if (isCandle && seriesIndex === 0) {
            const o = w.globals.seriesCandleO[seriesIndex]?.[dataPointIndex];
            const h = w.globals.seriesCandleH[seriesIndex]?.[dataPointIndex];
            const l = w.globals.seriesCandleL[seriesIndex]?.[dataPointIndex];
            const c = w.globals.seriesCandleC[seriesIndex]?.[dataPointIndex];
            return buildCandleTooltip(o, h, l, c, timeStr, evalData);
          }

          // Line/area price series
          if (!isCandle && seriesIndex === 0) {
            const closeVal = w.globals.series[seriesIndex]?.[dataPointIndex];
            return buildLineTooltip(closeVal, timeStr, evalData);
          }

          // Prediction lines — simple label
          const val = w.globals.series[seriesIndex]?.[dataPointIndex];
          if (val == null) return `<div style="padding:0"></div>`;
          const name = w.globals.seriesNames?.[seriesIndex] || "";
          return `<div style="background:${C.bg1};border:1px solid ${C.border};
            padding:6px 10px;font-family:${C.font};font-size:10px;color:${C.body}">
            <div style="color:${C.meta};margin-bottom:2px">${timeStr}</div>
            <div>${name}: <span style="color:${C.text}">$${fmt(val)}</span></div>
          </div>`;
        },
      },
      legend: { show: true, position: "top", labels: { colors: C.meta },
                markers: { width: 10, height: 10 }, fontFamily: C.font, fontSize: "10px" },
      annotations: positionAnnotations,
    };
  }, [chartType, xRange, positionAnnotations, evalMap]);

  // ── Series ────────────────────────────────────────────────────────────────
  const series = useMemo(() => {
    const isCandle  = chartType === "candlestick";
    const priceType = chartType === "area" ? "area" : isCandle ? "candlestick" : "line";
    return [
      { name: "Price",        type: priceType, data: isCandle ? ohlc : closeLine },
      { name: "Long Signal",  type: "line",    data: up,   color: C.green },
      { name: "Short Signal", type: "line",    data: down, color: C.red   },
      { name: "Target Price", type: "line",    data: pv,   color: C.amber },
    ];
  }, [chartType, ohlc, closeLine, up, down, pv]);

  // ── Derived header values ─────────────────────────────────────────────────
  const isClosed    = selectedPosition && selectedPosition.exitTime;
  const { adjustments, currentTarget, currentStop, originalTarget, originalStop } = selectedPositionData;
  const latestClose = latestData.close ? parseFloat(latestData.close) : null;
  const prevClose   = data && data.length > 1
    ? parseFloat(data[data.length - 2]?.close || latestClose) : latestClose;
  const priceUp     = latestClose != null && latestClose >= (prevClose ?? latestClose);

  // ── Loading state ─────────────────────────────────────────────────────────
  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <div style={{ background: C.bg1, border: `1px solid ${C.border}`, borderRadius: "8px",
        height: "460px", display: "flex", alignItems: "center", justifyContent: "center",
        fontFamily: C.font, color: C.meta, fontSize: "12px" }}>
        Loading market data...
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{ background: C.bg1, border: `1px solid ${C.border}`,
      borderRadius: "8px", padding: "12px 14px", fontFamily: C.font }}>

      {/* Position info strip */}
      {selectedPosition && (
        <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: "6px",
          padding: "8px 12px", marginBottom: "10px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: "8px", flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
            <span style={{ fontSize: "13px", fontWeight: 600, color: C.text }}>
              {selectedPosition.symbol}
            </span>
            <Pill color={selectedPosition.isLong ? C.green : C.red}>
              {selectedPosition.isLong ? "LONG" : "SHORT"}
            </Pill>
            {isClosed && <Pill color={selectedPosition.isWin ? C.green : C.red}>
              {selectedPosition.isWin ? "WIN" : "LOSS"}
            </Pill>}
            {adjustments.length > 0 && <Pill color={C.amber}>{adjustments.length} ADJ</Pill>}
          </div>
          <div style={{ fontSize: "10px", color: C.meta }}>
            Entry ${selectedPosition.entryPrice?.toFixed(2)}
            {isClosed && ` · Exit $${selectedPosition.exitPrice?.toFixed(2)}`}
            {selectedPosition.unrealizedPL != null &&
              ` · P&L $${selectedPosition.unrealizedPL.toFixed(2)}`}
            {adjustments.length > 0 && currentTarget && currentStop && (
              <span style={{ color: C.amber, marginLeft: "8px" }}>
                T ${originalTarget.toFixed(2)} → ${currentTarget.toFixed(2)}&nbsp;
                S ${originalStop.toFixed(2)} → ${currentStop.toFixed(2)}
              </span>
            )}
          </div>
          <div style={{ fontSize: "10px", color: C.meta }}>
            {new Date(selectedPosition.entryTime).toLocaleString("en-US",
              { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
          </div>
        </div>
      )}

      {/* GenAI signal strip */}
      {!selectedPosition && genAI.RecommendedAction && (
        <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: "6px",
          padding: "6px 12px", marginBottom: "10px",
          display: "flex", alignItems: "center", gap: "8px" }}>
          <span style={{ fontSize: "10px", color: C.meta }}>AI SIGNAL</span>
          <Pill color={genAI.RecommendedAction === "BUY" ? C.green :
                       genAI.RecommendedAction === "SELL" ? C.red : C.meta}>
            {genAI.RecommendedAction}
          </Pill>
          <span style={{ fontSize: "10px", color: C.meta }}>{genAI.Confidence || 0}%</span>
          {genAI.entry_price && (
            <span style={{ fontSize: "10px", color: C.meta, marginLeft: "auto" }}>
              Entry ${genAI.entry_price}
              {genAI.take_profit && ` · Target $${genAI.take_profit}`}
              {genAI.stop_loss   && ` · Stop $${genAI.stop_loss}`}
            </span>
          )}
        </div>
      )}

      {/* ── Subtitle + guide trigger — above chart ──────────────────────── */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: "12px", marginBottom: "10px",
      }}>
        <span style={{ fontSize: "11px", color: C.body, lineHeight: "1.5" }}>
          AI-driven markers · hover any bar for full reasoning ·{" "}
          <span style={{ color: C.cyan }}>S/R lines from agent pattern analysis</span>
        </span>
        <button
          onClick={() => setShowGuide(true)}
          style={{
            flexShrink: 0, background: "transparent",
            border: `1px solid ${C.cyan}60`, borderRadius: "4px",
            color: C.cyan, fontFamily: C.font, fontSize: "9px",
            padding: "3px 10px", cursor: "pointer",
            letterSpacing: "0.05em", whiteSpace: "nowrap",
            transition: "border-color 0.15s, background 0.15s",
          }}
          onMouseEnter={e => { e.currentTarget.style.background = C.cyan + "15"; e.currentTarget.style.borderColor = C.cyan; }}
          onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = C.cyan + "60"; }}
        >
          HOW TO READ THIS CHART
        </button>
      </div>

      {/* Chart header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
        marginBottom: "8px", flexWrap: "wrap", gap: "6px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span style={{ fontSize: "10px", color: C.meta,
            letterSpacing: "0.06em", textTransform: "uppercase" }}>Market</span>
          {(latestData.symbol || selectedPosition?.symbol) && (
            <span style={{ fontSize: "13px", fontWeight: 600, color: C.text }}>
              {latestData.symbol || selectedPosition?.symbol}
            </span>
          )}
          {latestClose != null && (
            <span style={{ fontSize: "14px", fontWeight: 600, color: priceUp ? C.green : C.red }}>
              ${latestClose.toFixed(2)}
              <span style={{ fontSize: "10px", marginLeft: "3px", opacity: 0.8 }}>
                {priceUp ? "▲" : "▼"}
              </span>
            </span>
          )}
          {latestEval?.regime && (
            <Pill color={regimeColor(latestEval.regime)}>
              {latestEval.regime.replace(/_/g, " ")}
            </Pill>
          )}
          <Pill color={C.meta}>{data.length} bars</Pill>
          {allAdjustmentsData.length > 0 && !selectedPosition && (
            <Pill color={C.amber}>{allAdjustmentsData.length} adj</Pill>
          )}
        </div>

        {/* Chart type toggle */}
        <div style={{ display: "flex", gap: "2px", background: C.bg,
          border: `1px solid ${C.border}`, borderRadius: "5px", padding: "2px" }}>
          {["candlestick", "line", "area"].map((t) => (
            <ToggleBtn key={t} label={t === "candlestick" ? "CANDLE" : t.toUpperCase()}
              active={chartType === t} onClick={() => setChartType(t)} />
          ))}
        </div>
      </div>

      {/* Main chart */}
      <div style={{ background: C.bg1 }}>
        <ReactApexChart
          key={chartType}
          options={chartOptions}
          series={series}
          type={chartType === "candlestick" ? "candlestick" : "line"}
          height={400}
        />
      </div>

      {/* Bottom controls */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
        marginTop: "8px", flexWrap: "wrap", gap: "6px" }}>
        <div style={{ display: "flex", gap: "2px", background: C.bg,
          border: `1px solid ${C.border}`, borderRadius: "5px", padding: "2px" }}>
          {[
            { key: "last30", label: "LAST 30" },
            { key: "last60", label: "LAST 60" },
            { key: "full",   label: "FULL DAY" },
          ].map(({ key, label }) => (
            <ToggleBtn key={key} label={label}
              active={viewWindow === key} onClick={() => setViewWindow(key)} />
          ))}
        </div>

        <span style={{ fontSize: "10px", color: C.meta }}>
          {selectedPosition ? "Position View" : "Session View"} · {data.length} bars
        </span>
      </div>

      {/* ── Slide-in guide panel ─────────────────────────────────────────── */}
      <ChartGuidePanel open={showGuide} onClose={() => setShowGuide(false)} />
    </div>
  );
};

export default memo(MarketChart);